import { Fragment, useEffect, useMemo, useState } from "react";
import {
  Columns3,
  List,
  Network,
  Plus,
  CalendarDays,
  Pencil,
  Info,
  ArrowRight,
  ExternalLink,
  FileText,
} from "lucide-react";
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState,
} from "@tanstack/react-table";
import { api } from "../api/client";
import { useSearch } from "../context/SearchContext";
import { useAuth } from "../context/AuthContext";
import { useRole } from "../context/RoleContext";
import { useT } from "../i18n";
import { NewProcessModal } from "../components/NewProcessModal";
import { formatDate } from "../utils/format";
import { WORK_STATUSES } from "../../shared/workflow";
import type { Catalogue, CatalogueRow } from "../types/catalogue";
import type { WorkItem } from "../types";

type View = "board" | "list" | "landscape";
type Row = {
  key: string;
  title: string;
  path: string | null;
  tier: string;
  domain: string;
  work: WorkItem | null;
  file: CatalogueRow | null;
};
const tierKey = (tier: string) =>
  ["unclassified", "core", "management", "support"][
    Number(tier.split(" ")[0])
  ] || "unclassified";
const domainSegments = (r: Row) =>
  r.domain
    .split("/")
    .map((s) => s.trim())
    .filter(Boolean);
const domainKey = (tier: string, segments: string[]) =>
  [tier, ...segments]
    .map((s) => s.trim())
    .filter(Boolean)
    .join("/");
const labelWithoutOrder = (label: string) => label.replace(/^\d+\s+/, "");
const href = (r: Row) =>
  r.work
    ? `#/process/${r.work.id}`
    : `#/process/file?path=${encodeURIComponent(r.path!)}`;

export function LandscapePage() {
  const { t, lang } = useT();
  const { isCde } = useRole();
  const { user } = useAuth();
  const { searchTerm, setSearchTerm } = useSearch();
  const preference = `cde.overview.${user?.id}.view`;
  const [view, setView] = useState<View>(() => {
    const v = localStorage.getItem(preference);
    return ["board", "list", "landscape"].includes(v ?? "")
      ? (v as View)
      : "board";
  });
  const [data, setData] = useState<Catalogue | null>(null),
    [work, setWork] = useState<WorkItem[]>([]);
  const [error, setError] = useState(""),
    [newOpen, setNewOpen] = useState(false);
  const [domain, setDomain] = useState(""),
    [client, setClient] = useState(""),
    [consultant, setConsultant] = useState(""),
    [kind, setKind] = useState(""),
    [status, setStatus] = useState(""),
    [phase, setPhase] = useState("");
  const [expandedCells, setExpandedCells] = useState<Set<string>>(new Set());
  const [closed, setClosed] = useState(false),
    [editing, setEditing] = useState<number | null>(null),
    [note, setNote] = useState(""),
    [busy, setBusy] = useState<number | null>(null);
  const load = async () => {
    const [c, w] = await Promise.all([api.getCatalogue(), api.getWork()]);
    setData(c);
    setWork(w);
  };
  useEffect(() => {
    load().catch((e) => setError(e.message));
  }, []);
  useEffect(() => {
    localStorage.setItem(preference, view);
  }, [view, preference]);
  const rows = useMemo(() => {
    const byPath = new Map(
      work.filter((w) => w.git_path).map((w) => [w.git_path, w]),
    );
    const result: Row[] = (data?.rows ?? [])
      .filter((f) => !f.isTechnical && f.kind === "file")
      .map((f) => ({
        key: f.path!,
        title: byPath.get(f.path)?.name || f.title,
        path: f.path,
        tier: f.tier,
        domain: f.areaSegments.join(" / "),
        work: byPath.get(f.path) ?? null,
        file: f,
      }));
    for (const w of work)
      if (!result.some((r) => r.work?.registry_id === w.registry_id))
        result.push({
          key: `work-${w.id}`,
          title: w.name,
          path: w.git_path,
          tier: w.git_tier ?? "",
          domain: (w.git_area ?? "").split("/").join(" / "),
          work: w,
          file: data?.rows.find((f) => f.record?.id === w.id) ?? null,
        });
    return result;
  }, [data, work]);
  const filtered = rows.filter((r) => {
    const w = r.work;
    if (!closed && w?.state === "cancelled") return false;
    if (
      (domain &&
        !(
          domainKey(r.tier, domainSegments(r)) === domain ||
          domainKey(r.tier, domainSegments(r)).startsWith(domain + "/")
        )) ||
      (client && w?.client !== client) ||
      (consultant && w?.consultant_name !== consultant) ||
      (kind && w?.kind !== kind)
    )
      return false;
    if (
      (status && (w?.work_status ?? "untracked") !== status) ||
      (phase && (w?.workflow_mode !== "full" || w.phase_key !== phase))
    )
      return false;
    const q = searchTerm.trim().toLocaleLowerCase();
    return (
      !q ||
      [
        r.title,
        r.path,
        r.domain,
        r.tier,
        w?.registry_code,
        w?.client,
        w?.consultant_name,
        w?.next_action,
        w?.pmo_project_name,
        r.file?.tracking?.notes,
      ].some((s) => s?.toLocaleLowerCase().includes(q))
    );
  });
  const board = filtered.filter(
    (r) =>
      r.work && (closed || !["cancelled", "superseded"].includes(r.work.state)),
  );
  const unique = (values: (string | null | undefined)[]) =>
    [...new Set(values.filter(Boolean) as string[])].sort((a, b) =>
      a.localeCompare(b, lang, { numeric: true }),
    );
  const domains = unique(
    rows.flatMap((r) =>
      domainSegments(r).map((_, i) =>
        domainKey(r.tier, domainSegments(r).slice(0, i + 1)),
      ),
    ),
  );
  const act = async (id: number, fn: () => Promise<any>) => {
    setBusy(id);
    setError("");
    try {
      await fn();
      await load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(null);
    }
  };
  const pickView = (v: View) => {
    setView(v);
    setError("");
  };
  const reset = () => {
    setDomain("");
    setClient("");
    setConsultant("");
    setKind("");
    setStatus("");
    setPhase("");
  };
  const renderBoardCard = (r: Row) => {
    const w = r.work!;
    const editable = isCde && ["active", "on_hold"].includes(w.state);
    const nextStatus = w.work_status === "inbox" ? "todo" : "in_progress";
    const subdomain =
      domainSegments(r).slice(1).map(labelWithoutOrder).join(" › ") ||
      t("control.noDomain");
    return (
      <article className="card control-card control-card--compact" key={w.id}>
        <a
          className="control-card-info"
          href={href(r)}
          title={t("control.open")}
          aria-label={`${t("control.open")} : ${r.title}`}
        >
          <Info size={14} />
        </a>
        <a className="control-card-summary" href={href(r)}>
          <span className="control-card-name">{r.title}</span>
          <span className="control-card-meta">
            {" "}
            —{" "}
            {w.client ||
              `${t("control.client")} ${t("control.unassigned").toLowerCase()}`}
          </span>
          <span className="control-card-meta"> — {w.registry_code}</span>
          <span className="control-card-meta">
            {" "}
            — CdE {w.consultant_name || t("control.unassigned")}
          </span>
        </a>
        <div className="control-card-bottom">
          <span className="control-subdomain" title={r.domain}>
            {subdomain}
          </span>
          <div className="control-card-footer">
            <span
              className="control-card-kind"
              title={
                w.workflow_mode === "full"
                  ? `${w.phase_order} · ${lang === "fr" ? w.phase_label_fr : w.phase_label_en}`
                  : t("control.lightRevision")
              }
            >
              {w.state === "on_hold"
                ? t("control.paused")
                : w.state === "cancelled"
                  ? t("control.cancelled")
                  : t(
                      w.kind === "rework"
                        ? "control.revision"
                        : "control.creation",
                    )}
            </span>
            <div className="control-card-tools">
              <button
                className="btn-icon"
                title={w.next_action || t("control.noAction")}
                aria-label={`${t("control.editNote")} : ${r.title}`}
                aria-expanded={editing === w.id}
                onClick={() => {
                  setEditing(editing === w.id ? null : w.id);
                  setNote(w.next_action || "");
                }}
              >
                <Pencil size={13} />
              </button>
              <a
                href={`${href(r)}?tab=meetings`}
                className="control-card-meetings"
                title={`${t("control.meetings")}${w.next_meeting || w.last_meeting ? ` · ${formatDate(w.next_meeting || w.last_meeting, lang)}` : ""}`}
                aria-label={`${t("control.meetings")} : ${w.meeting_count || 0} — ${r.title}`}
              >
                <CalendarDays size={13} />
                <span>{w.meeting_count || 0}</span>
              </a>
              {w.pmo_project_id && (
                <a
                  className="btn-icon"
                  href={`https://pmodev.sintra.ch/#project?id=${w.pmo_project_id}`}
                  target="_blank"
                  rel="noreferrer"
                  title={`PMO : ${w.pmo_project_name || w.pmo_project_id}`}
                  aria-label={`PMO : ${w.pmo_project_name || w.pmo_project_id}`}
                >
                  <ExternalLink size={13} />
                </a>
              )}
              {isCde &&
                w.state === "active" &&
                ["inbox", "todo"].includes(w.work_status) && (
                  <button
                    className="btn-icon"
                    disabled={busy === w.id}
                    title={`${t("control.move")} ${t(`control.${nextStatus}`)}`}
                    aria-label={`${t("control.move")} ${t(`control.${nextStatus}`)} : ${r.title}`}
                    onClick={() =>
                      act(w.id, () => api.moveWork(w.id, nextStatus))
                    }
                  >
                    <ArrowRight size={13} />
                  </button>
                )}
            </div>
          </div>
        </div>
        {editing === w.id && (
          <div className="control-card-note">
            {editable ? (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  act(w.id, async () => {
                    await api.updateProcess(w.id, { next_action: note });
                    setEditing(null);
                  });
                }}
              >
                <label className="form-label" htmlFor={`note-${w.id}`}>
                  {t("control.nextAction")}
                </label>
                <textarea
                  id={`note-${w.id}`}
                  className="form-textarea"
                  rows={3}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
                <div className="flex gap-8 mt-8">
                  <button
                    className="btn btn-accent btn-sm"
                    disabled={busy === w.id}
                  >
                    {t("common.save")}
                  </button>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => setEditing(null)}
                  >
                    {t("common.cancel")}
                  </button>
                </div>
              </form>
            ) : (
              <p>{w.next_action || t("control.noAction")}</p>
            )}
          </div>
        )}
      </article>
    );
  };
  const boardLanes = ["core", "management", "support", "unclassified"]
    .filter(
      (key) =>
        key !== "unclassified" || board.some((r) => tierKey(r.tier) === key),
    )
    .map((key) => {
      const laneRows = board.filter((r) => tierKey(r.tier) === key);
      const taxonomyRows = rows.filter((r) => tierKey(r.tier) === key);
      const root = taxonomyRows[0]?.tier || "";
      return { key, root, rows: laneRows };
    })
    .filter((lane) => !domain || tierKey(domain.split("/")[0]) === lane.key);
  const columns: ColumnDef<Row>[] = [
    {
      id: "code",
      accessorFn: (r) => r.work?.registry_code || "—",
      header: t("rep.colCode"),
    },
    {
      accessorKey: "title",
      header: t("rep.col.process"),
      cell: ({ row: { original: r } }) => (
        <a
          className="text-accent font-medium control-list-title"
          href={href(r)}
          title={r.title}
        >
          {r.title}
        </a>
      ),
    },
    {
      id: "status",
      accessorFn: (r) => r.work?.work_status || "untracked",
      header: t("rep.col.state"),
      cell: ({ row: { original: r } }) => (
        <span
          className="badge"
          title={
            r.work?.workflow_mode === "full"
              ? `${r.work.phase_order} · ${lang === "fr" ? r.work.phase_label_fr : r.work.phase_label_en}`
              : undefined
          }
        >
          {r.work?.state === "on_hold"
            ? t("control.paused")
            : r.work?.state === "cancelled"
              ? t("control.cancelled")
              : r.work
                ? t(`control.${r.work.work_status}`)
                : t("control.qualify")}
        </span>
      ),
    },
    {
      id: "consultant",
      accessorFn: (r) => r.work?.consultant_name || t("control.unassigned"),
      header: t("control.consultant"),
    },
    {
      id: "client",
      accessorFn: (r) => r.work?.client || "—",
      header: t("control.client"),
    },
    {
      id: "domain",
      accessorFn: (r) =>
        [r.tier.replace(/^\d+ /, ""), r.domain].filter(Boolean).join(" / "),
      header: t("control.domain"),
    },
    {
      id: "historical",
      accessorFn: (r) => r.file?.tracking?.progress ?? -1,
      header: t("land.modelling"),
      cell: ({ row: { original: r } }) =>
        r.file?.tracking ? (
          <span
            title={`${t("land.sourceDate", { date: data?.tracking.asOf || "" })}\n${r.file.tracking.notes}`}
          >
            {r.file.tracking.progress}%
          </span>
        ) : (
          "—"
        ),
    },
    {
      id: "version",
      accessorFn: (r) => r.work?.published_version || "—",
      header: t("control.published"),
    },
    {
      id: "preparing",
      accessorFn: (r) =>
        r.work?.state === "active" ? r.work.target_version || "—" : "—",
      header: t("control.preparing"),
    },
    {
      id: "notes",
      header: t("land.notes"),
      cell: ({ row: { original: r } }) => (
        <a
          href={`${href(r)}${r.work ? "?tab=notes" : ""}`}
          className="btn-icon"
          title={
            r.file?.tracking?.notes ||
            r.work?.next_action ||
            t("land.noTracking")
          }
          aria-label={`${t("land.notes")} : ${r.title}`}
        >
          <FileText size={14} />
        </a>
      ),
    },
  ];
  const [sorting, setSorting] = useState<SortingState>([
    { id: "title", desc: false },
  ]);
  const table = useReactTable({
    data: filtered,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });
  if (!data && !error)
    return (
      <div className="page-loading">
        <div className="spinner" />
      </div>
    );
  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">{t("land.title")}</h1>
          <p className="page-meta">{t("control.subtitle")}</p>
        </div>
        <div className="flex gap-8">
          <a className="btn" href="#/repository">
            {t("land.repository")}
          </a>
          {isCde && (
            <button className="btn btn-accent" onClick={() => setNewOpen(true)}>
              <Plus className="icon-sm" />
              {t("control.new")}
            </button>
          )}
        </div>
      </div>
      <div className="stats-grid mb-24">
        {["core", "management", "support"].map((tier, i) => (
          <div className="stat-card" key={tier}>
            <div className="stat-value">
              {rows.filter((r) => r.tier.startsWith(`${i + 1} `)).length}
            </div>
            <div className="stat-label">{t(`land.tier.${tier}`)}</div>
          </div>
        ))}
      </div>
      <div className="tabs" role="tablist" aria-label={t("land.title")}>
        {(
          [
            ["board", Columns3],
            ["list", List],
            ["landscape", Network],
          ] as const
        ).map(([v, Icon]) => (
          <button
            key={v}
            className={`tab control-view-tab${view === v ? " active" : ""}`}
            role="tab"
            aria-selected={view === v}
            onClick={() => pickView(v)}
          >
            <Icon className="icon-sm" />
            {t(`control.${v}`)}
          </button>
        ))}
      </div>
      <div className="toolbar control-filters">
        <input
          type="search"
          className="form-input control-mobile-search"
          aria-label={t("shell.search")}
          placeholder={t("shell.search")}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <select
          className="form-select"
          aria-label={t("control.domain")}
          value={domain}
          onChange={(e) => setDomain(e.target.value)}
        >
          <option value="">{t("control.allDomains")}</option>
          {domains.map((d) => (
            <option key={d} value={d}>
              {d.replaceAll("/", " › ")}
            </option>
          ))}
        </select>
        <select
          className="form-select"
          aria-label={t("control.client")}
          value={client}
          onChange={(e) => setClient(e.target.value)}
        >
          <option value="">{t("control.allClients")}</option>
          {unique(work.map((w) => w.client)).map((c) => (
            <option key={c}>{c}</option>
          ))}
        </select>
        <select
          className="form-select"
          aria-label={t("control.consultant")}
          value={consultant}
          onChange={(e) => setConsultant(e.target.value)}
        >
          <option value="">{t("control.allConsultants")}</option>
          {unique(work.map((w) => w.consultant_name)).map((c) => (
            <option key={c}>{c}</option>
          ))}
        </select>
        <select
          className="form-select"
          aria-label={t("control.type")}
          value={kind}
          onChange={(e) => setKind(e.target.value)}
        >
          <option value="">{t("control.type")}</option>
          <option value="new">{t("control.creation")}</option>
          <option value="rework">{t("control.revision")}</option>
        </select>
        <select
          className="form-select"
          aria-label={t("control.status")}
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          <option value="">{t("control.status")}</option>
          {WORK_STATUSES.map((s) => (
            <option key={s} value={s}>
              {t(`control.${s}`)}
            </option>
          ))}
          <option value="untracked">{t("control.qualify")}</option>
        </select>
        <select
          className="form-select"
          aria-label={t("control.phase")}
          value={phase}
          onChange={(e) => setPhase(e.target.value)}
        >
          <option value="">{t("control.allPhases")}</option>
          {unique(
            work
              .filter((w) => w.workflow_mode === "full")
              .map((w) => w.phase_key),
          ).map((p) => (
            <option key={p} value={p}>
              {work.find((w) => w.phase_key === p)?.phase_label_fr || p}
            </option>
          ))}
        </select>
        {(domain || client || consultant || kind || status || phase) && (
          <button className="btn btn-ghost btn-sm" onClick={reset}>
            {t("control.reset")}
          </button>
        )}
      </div>
      <div className="flex justify-between gap-8 mb-16">
        <p className="text-xs text-ink-3">
          {view === "board"
            ? t("control.boardHint")
            : t("control.count", { n: filtered.length })}
        </p>
        <label className="text-xs text-ink-3">
          <input
            type="checkbox"
            checked={closed}
            onChange={(e) => setClosed(e.target.checked)}
          />{" "}
          {t("control.includeClosed")}
        </label>
      </div>
      {error && (
        <div className="card text-err" role="alert">
          {error}{" "}
          {!data && (
            <button
              className="btn"
              onClick={() => load().catch((e) => setError(e.message))}
            >
              {t("land.retry")}
            </button>
          )}
        </div>
      )}
      {view === "board" && (
        <div
          className="matrix-grid-container control-matrix-scroll"
          aria-label={t("control.boardLandscape")}
        >
          <div className="matrix-grid control-matrix">
            <div className="matrix-grid-corner">{t("control.category")}</div>
            {WORK_STATUSES.map((status) => (
              <div
                key={status}
                className={`matrix-grid-col control-board-status control-board-status--${status}`}
              >
                <span>{t(`control.${status}`)}</span>
                <span className="control-domain-count">
                  {board.filter((r) => r.work!.work_status === status).length}
                </span>
              </div>
            ))}
            {boardLanes.map((lane) => (
              <Fragment key={lane.key}>
                <div
                  className="matrix-grid-row control-matrix-category"
                  data-tier={lane.key}
                >
                  {t(`land.tier.${lane.key}`)}
                </div>
                {WORK_STATUSES.map((status) => {
                  const inCell = lane.rows
                    .filter((r) => r.work!.work_status === status)
                    .sort(
                      (a, b) =>
                        a.domain.localeCompare(b.domain, lang, {
                          numeric: true,
                        }) || a.title.localeCompare(b.title, lang),
                    );
                  const key = `${lane.key}:${status}`,
                    expanded = expandedCells.has(key);
                  const shown = expanded ? inCell : inCell.slice(0, 4);
                  const groups = unique(
                    shown.map(
                      (r) => domainSegments(r)[0] || t("control.noDomain"),
                    ),
                  );
                  return (
                    <div
                      className="matrix-grid-cell control-matrix-cell"
                      data-tier={lane.key}
                      data-status={status}
                      key={status}
                      aria-label={`${t(`land.tier.${lane.key}`)} · ${t(`control.${status}`)}`}
                    >
                      {groups.map((name) => (
                        <div className="control-domain-group" key={name}>
                          <button
                            className="control-domain-heading"
                            title={name}
                            aria-pressed={
                              domain === domainKey(lane.root, [name])
                            }
                            onClick={() => {
                              const key = domainKey(lane.root, [name]);
                              setDomain(domain === key ? "" : key);
                            }}
                          >
                            {labelWithoutOrder(name)}
                          </button>
                          {shown
                            .filter(
                              (r) =>
                                (domainSegments(r)[0] ||
                                  t("control.noDomain")) === name,
                            )
                            .map(renderBoardCard)}
                        </div>
                      ))}
                      {inCell.length > 4 && (
                        <button
                          className="btn btn-ghost btn-sm control-cell-expand"
                          aria-expanded={expanded}
                          onClick={() =>
                            setExpandedCells((old) => {
                              const next = new Set(old);
                              next.has(key) ? next.delete(key) : next.add(key);
                              return next;
                            })
                          }
                        >
                          {expanded
                            ? t("control.collapseCards")
                            : t("control.moreCards", { n: inCell.length - 4 })}
                        </button>
                      )}
                      {inCell.length === 0 && (
                        <span
                          className="control-lane-empty"
                          aria-label={t("control.noCards")}
                        >
                          —
                        </span>
                      )}
                    </div>
                  );
                })}
              </Fragment>
            ))}
          </div>
        </div>
      )}
      {view === "list" && (
        <div
          className="landscape-table control-list-scroll"
          role="region"
          tabIndex={0}
          aria-label={t("control.list")}
        >
          <table className="table control-list">
            <thead>
              {table.getHeaderGroups().map((g) => (
                <tr key={g.id}>
                  {g.headers.map((h) => (
                    <th
                      key={h.id}
                      aria-sort={
                        h.column.getIsSorted() === "asc"
                          ? "ascending"
                          : h.column.getIsSorted() === "desc"
                            ? "descending"
                            : "none"
                      }
                    >
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={h.column.getToggleSortingHandler()}
                      >
                        {flexRender(h.column.columnDef.header, h.getContext())}
                        {h.column.getIsSorted() === "asc"
                          ? " ↑"
                          : h.column.getIsSorted() === "desc"
                            ? " ↓"
                            : ""}
                      </button>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((r) => (
                <tr key={r.id}>
                  {r.getVisibleCells().map((c) => (
                    <td key={c.id} title={String(c.getValue() ?? "")}>
                      {flexRender(c.column.columnDef.cell, c.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {view === "landscape" &&
        [1, 2, 3, 0].map((n) => {
          const inTier = filtered.filter((r) =>
            n ? r.tier.startsWith(`${n} `) : !/^([123]) /.test(r.tier),
          );
          if (!inTier.length) return null;
          return (
            <section key={n} className="mb-24">
              <h2 className="rep-tier-head">
                {t(
                  `land.tier.${["unclassified", "core", "management", "support"][n]}`,
                )}
              </h2>
              {unique(inTier.map((r) => r.domain || t("control.noDomain"))).map(
                (d) => (
                  <div key={d}>
                    <h3 className="rep-area-head">{d}</h3>
                    <div className="control-landscape-domain">
                      {inTier
                        .filter(
                          (r) => (r.domain || t("control.noDomain")) === d,
                        )
                        .sort((a, b) => a.title.localeCompare(b.title))
                        .map((r) => (
                          <a
                            key={r.key}
                            href={href(r)}
                            className="card control-landscape-process"
                          >
                            <span className="font-medium">{r.title}</span>
                            <span className="text-xs text-ink-3">
                              {r.work
                                ? `${r.work.registry_code} · ${t(`control.${r.work.work_status}`)}`
                                : t("control.qualify")}
                            </span>
                          </a>
                        ))}
                    </div>
                  </div>
                ),
              )}
            </section>
          );
        })}
      {view !== "board" && !filtered.length && (
        <div className="empty-state">{t("land.empty")}</div>
      )}
      {view !== "board" && data?.tracking && (
        <details className="card mt-16">
          <summary>
            {t("land.reconciliation")} · {data.tracking.asOf}
          </summary>
          <p className="text-sm mt-12">{t("land.snapshotHint")}</p>
          <p className="text-sm">
            {t("land.snapshot", {
              date: data.tracking.asOf,
              total: data.tracking.total,
              matched: data.tracking.matched,
            })}
          </p>
          <ul>
            {data.tracking.unmatched.map((r) => (
              <li key={r.sourceRow}>
                {r.name} · {r.progress}% {r.notes}
              </li>
            ))}
          </ul>
        </details>
      )}
      {newOpen && (
        <NewProcessModal
          onClose={() => setNewOpen(false)}
          onCreated={(id) => {
            window.location.hash = `#/process/${id}`;
          }}
        />
      )}
    </>
  );
}
